import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => (d && d.length >= 10 ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : (d || ''));
const fmtMoney = (n) => `€${Number(n || 0).toFixed(2)}`;

/**
 * Phase 735.46 — Cleaner Statement Ledger tab.
 * Running-balance ledger merged from invoices (debit) + payments
 * (credit). Filters: from / to / month / year. Summary shows total
 * invoiced, total paid, outstanding.
 */
export const CleanerStatementTab = ({ cleanerId, axCfg, t }) => {
  const [data, setData] = useState({ entries: [], total_invoiced: 0, total_paid: 0, outstanding: 0 });
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');

  const fetchLedger = useCallback(async () => {
    if (!cleanerId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (dateFrom) qs.set('date_from', dateFrom);
      if (dateTo) qs.set('date_to', dateTo);
      if (year && !dateFrom && !dateTo) qs.set('year', year);
      if (month && !dateFrom && !dateTo) qs.set('month', month);
      const res = await axios.get(`${API}/cleaning/cleaners/${cleanerId}/statement-ledger?${qs.toString()}`, axCfg);
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [cleanerId, dateFrom, dateTo, year, month, axCfg]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  return (
    <div className="space-y-3" data-testid={`cleaner-statement-tab-${cleanerId}`}>
      {/* Filters */}
      <div className="flex items-end gap-3 flex-wrap border border-zinc-200 bg-white px-3 py-2" data-testid="statement-filters">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('from') || 'From'}</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                 className="rounded-none h-8 text-xs" data-testid="statement-date-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('to') || 'To'}</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                 className="rounded-none h-8 text-xs" data-testid="statement-date-to" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('year') || 'Year'}</Label>
          <Input type="number" min="2020" max="2099" value={year} onChange={(e) => setYear(e.target.value)}
                 className="rounded-none h-8 text-xs w-24" placeholder="YYYY" data-testid="statement-year" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('month') || 'Month'}</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="rounded-none h-8 text-xs" data-testid="statement-month" />
        </div>
        <button
          onClick={() => { setDateFrom(''); setDateTo(''); setYear(''); setMonth(''); }}
          className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-800 border border-zinc-200 px-2 h-8"
          data-testid="statement-filters-clear"
        >{t('clear') || 'Clear'}</button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 border border-zinc-200 bg-zinc-100" data-testid="statement-summary">
        <div className="bg-white px-3 py-2 border-r border-zinc-100">
          <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold">{t('totalInvoiced') || 'Total Invoiced'}</div>
          <div className="text-lg font-mono font-bold tabular-nums text-zinc-900" data-testid="statement-total-invoiced">{fmtMoney(data.total_invoiced)}</div>
        </div>
        <div className="bg-white px-3 py-2 border-r border-zinc-100">
          <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold">{t('totalPaid') || 'Total Paid'}</div>
          <div className="text-lg font-mono font-bold tabular-nums text-emerald-700" data-testid="statement-total-paid">{fmtMoney(data.total_paid)}</div>
        </div>
        <div className="bg-white px-3 py-2">
          <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold">{t('outstandingBalance') || 'Outstanding Balance'}</div>
          <div className={`text-lg font-mono font-bold tabular-nums ${Number(data.outstanding || 0) > 0.005 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="statement-outstanding">{fmtMoney(data.outstanding)}</div>
        </div>
      </div>

      {/* Ledger */}
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="statement-ledger-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 bg-zinc-50">
              <th className="text-left px-3 py-2 w-24">{t('date') || 'Date'}</th>
              <th className="text-left px-3 py-2 w-32">{t('reference') || 'Reference'}</th>
              <th className="text-left px-3 py-2 w-24">{t('type') || 'Type'}</th>
              <th className="text-left px-3 py-2">{t('description') || 'Description'}</th>
              <th className="text-right px-3 py-2 w-24">{t('debit') || 'Debit'}</th>
              <th className="text-right px-3 py-2 w-24">{t('credit') || 'Credit'}</th>
              <th className="text-right px-3 py-2 w-28">{t('runningBalance') || 'Running Balance'}</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-6 text-zinc-400 text-xs">{t('loading') || 'Loading…'}</td></tr>}
            {!loading && data.entries.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-zinc-400 text-xs" data-testid="statement-empty">
                {t('noStatementEntries') || 'No entries in this period.'}
              </td></tr>
            )}
            {!loading && data.entries.map((e, i) => {
              const isInvoice = e.type === 'invoice';
              return (
                <tr key={`${e.type}-${e.reference}-${i}`} className="border-b border-zinc-100 hover:bg-zinc-50/60"
                    data-testid={`statement-row-${i}`}>
                  <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-3 py-1.5 font-mono text-xs">{e.reference}</td>
                  <td className="px-3 py-1.5">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${isInvoice ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}
                          data-testid={`statement-type-${i}`}>
                      {isInvoice ? (t('invoice') || 'Invoice') : (t('payment') || 'Payment')}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-xs text-zinc-700 max-w-[380px] truncate" title={e.description || ''}>
                    {e.description || '—'}
                    {!isInvoice && e.unallocated_amount > 0.005 && (
                      <span className="ml-2 text-[9px] uppercase tracking-wider px-1 py-0.5 bg-amber-50 text-amber-800 border border-amber-200"
                            data-testid={`statement-unalloc-${i}`}>
                        {t('unallocated') || 'Unallocated'} {fmtMoney(e.unallocated_amount)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{isInvoice ? fmtMoney(e.debit) : '—'}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-emerald-700">{!isInvoice ? fmtMoney(e.credit) : '—'}</td>
                  <td className={`px-3 py-1.5 text-right font-mono tabular-nums font-semibold ${e.running_balance > 0.005 ? 'text-red-700' : (e.running_balance < -0.005 ? 'text-emerald-700' : 'text-zinc-700')}`}>
                    {fmtMoney(e.running_balance)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CleanerStatementTab;
